import { ReportStudent, ReportingMixpanelEvent, TeacherClassroomMaps } from '@peekapak/types/ReportDataExchangeTypes';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { EventName } from '@peekapak/types/AnalyticsTypes';
import associationData from '../../ClassroomDashboard/Associations.json';
import { moodMap as elementaryMoodIndexMap } from '../../ClassroomDashboard/PeekavilleDataHelpers';
import { values as secondaryMoodWheel } from '../../CheckIn/checkInValues';
import {
  getCurrentYearTeachersData,
  getLegacyLessonsAccessed,
  getLegacyStoryOpenEvents,
  getLegacyStoryOpens,
  getNewLessonsAccessed,
  getNewStoryOpenEvents,
  getNewStoryOpens,
  getNumberOfEducators,
  getNumberOfStudents,
  lookupNewDBStartDate,
} from '../reportUtilities';

export const ELEMENTARY_MOODS = ['Excited', 'Happy', 'Calm', 'Nervous', 'Sad', 'Angry'];
export const SECONDARY_MOOD_CATEGORIES = ['Surprised', 'Confident', 'Happy', 'Calm', 'Sad', 'Scared', 'Angry'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekdaysInOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export interface SchoolDeckMetrics {
  schoolName: string;
  dateRangeLabel: string;
  overview: {
    staff: number;
    students: number;
    lessons: number;
    activities: number;
    stories: number;
    checkins: number;
    assignments: number;
  };
  educatorLoginsTotal: number;
  studentLoginsTotal: number;
  educatorLoginsByMonth: { month: string; logins: number }[];
  studentLoginsByMonth: { month: string; logins: number }[];
  educatorDayOfWeek: { day: string; count: number }[];
  mostUsedDay: string;
  topEducators: { name: string; logins: number }[];
  elementaryMoods: { mood: string; percent: number }[];
  elementaryUpMoodPercent: number;
  elementaryStudents: number;
  elementaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  secondaryMoods: { mood: string; percent: number }[];
  secondaryUpMoodPercent: number;
  secondaryStudents: number;
  secondaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  totalCheckIns: number;
  topGames: { name: string; plays: number }[];
  mostPopularGame: string;
  topStories: { name: string; reads: number }[];
}

export interface SchoolDeckMetricsInput {
  schoolName: string;
  startDate: Date | null;
  endDate: Date | null;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  newMixpanelEvents: ReportingMixpanelEvent[];
  gameProgressMap: Record<string, unknown>;
  assignmentsMap: Record<string, Assignment[]>;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherClassroomMaps: TeacherClassroomMaps;
  userList: UserProfileType[];
  // Per-student mood check-ins, keyed by userId. This is the same source the school-wide moodboard and
  // the "Down Moods"/"Mood Data" Excel sheets use - unlike the Mixpanel event lists above, it also covers
  // older date ranges whose journal entries live only in DynamoDB (see SchoolReport.tsx's journalsMap
  // loading effect), so it's the only complete source for mood data spanning both eras.
  journalsMap: Record<string, JournalMapEntry[]>;
}

export type MoodLogEvent = ReportingMixpanelEvent & {
  properties?: {
    mood?: string | number;
    grade?: number | string;
    timestamp?: number;
    unit?: string;
    story?: string;
    quest?: string;
    book?: number | string;
  };
  mood?: string | number;
  grade?: number | string;
  book?: number | string;
  unit?: string;
  story?: string;
  quest?: string;
};

// A journalsMap entry is one of two shapes depending on where it was sourced from:
//  - a raw Mixpanel 'journal-mood-log' event (recent date ranges, read straight from newMixpanelEvents)
//  - a legacy DynamoDB PeekavilleJournalEntry (older date ranges, fetched directly - never touches Mixpanel)
// The two shapes use different field names for the same concepts (time vs createdAt, mood vs feeling),
// and the legacy shape also carries non-mood entry kinds ('struggle', 'comprehension') that must be
// excluded via its `type` field.
export interface JournalMapEntry {
  name?: string;
  time?: number;
  createdAt?: number;
  feeling?: string | number;
  mood?: string | number;
  type?: string;
  grade?: number | string;
  properties?: { mood?: string | number; grade?: number | string };
}

const pad2 = (n: number) => String(n).padStart(2, '0');

export const monthsInRange = (start: Date | null | undefined, end: Date | null | undefined): string[] => {
  if (!start || !end || end < start) return [];
  const keys: string[] = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endKey = `${end.getUTCFullYear()}-${pad2(end.getUTCMonth() + 1)}`;
  for (;;) {
    const key = `${cursor.getUTCFullYear()}-${pad2(cursor.getUTCMonth() + 1)}`;
    keys.push(key);
    if (key >= endKey) break;
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return keys;
};

export const monthKeyOf = (time: number) => {
  const d = new Date(time);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
};

export const monthLabelOf = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).replace(' ', '-');
};

// These mirror getLegacyEducatorLogins/getNewEducatorLogins/getLegacyStudentLogins/getNewStudentLogins
// (reportUtilities.ts) predicate-for-predicate, kept as two separate legacy/new checks (rather than a
// single OR'd predicate) specifically so a login event matching both is counted twice, same as the
// on-page report - it sums getLegacy...(events) + getNew...(events) over the same array, and the two
// checks aren't always mutually exclusive (e.g. an educator 'login' event at/before the legacy cutoff
// matches both). A single deduped predicate would silently undercount relative to the on-page report.
const isLegacyEducatorLoginEvent = (event: ReportingMixpanelEvent): boolean =>
  new Date(event.time) <= lookupNewDBStartDate('peekapakLogin') &&
  ['User Login', 'login', 'User Reactivated'].includes(event.name);

const isNewEducatorLoginEvent = (event: ReportingMixpanelEvent): boolean =>
  ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(event.type) && event.name === EventName.LOGIN;

const STUDENT_LEGACY_LOGIN_NAMES = new Set(['user login', 'User Login', 'interaction end', 'User Reactivated']);
const STUDENT_LEGACY_EXPIRED_NAMES = new Set(['user login', 'User Login', 'interaction end']);
const STUDENT_LEGACY_EXPIRY_CUTOFF = new Date('2023-10-01T00:00:00.000Z');

export const isLegacyStudentLoginEvent = (event: ReportingMixpanelEvent): boolean => {
  if (event.type !== 'Student' || new Date(event.time) > lookupNewDBStartDate('completeDate')) return false;
  if (STUDENT_LEGACY_EXPIRED_NAMES.has(event.name) && new Date(event.time) >= STUDENT_LEGACY_EXPIRY_CUTOFF) {
    return false;
  }
  return STUDENT_LEGACY_LOGIN_NAMES.has(event.name);
};

export const isNewStudentLoginEvent = (event: ReportingMixpanelEvent): boolean => {
  if (event.type !== 'Student' || event.name !== EventName.LOGIN) return false;
  const isNewPeekaville =
    new Date(event.time) >= lookupNewDBStartDate('completeDate') && event.source === 'mypeekaville';
  const isNewPlatform = new Date(event.time) >= lookupNewDBStartDate('peekapakLogin') && event.source === 'peekapak';
  return isNewPeekaville || isNewPlatform;
};

// Legacy myPeekaville (PK-5) check-ins store the mood as a numeric index into this array rather than
// the category name itself, e.g. 2 === 'Excited'. Mirrors moodboardUtilities.getEachMoodCheckInElementary,
// which is the source of truth for the school-wide moodboard this deck is meant to match - a report that
// only accepted string mood values would silently drop every legacy myPeekaville check-in.
export const resolveElementaryMood = (mood: unknown): string | undefined => {
  if (typeof mood === 'string') return mood;
  if (typeof mood === 'number') return elementaryMoodIndexMap[mood];
  return undefined;
};

// A 6-12 check-in's mood is a sub-feeling of the seven-category wheel (e.g. 'Anxious'), not the
// category name itself. Mirrors moodboardUtilities.getEachMoodCheckInSecondary - looking up the
// category directly from the wheel (rather than a separately-maintained map) guarantees this stays
// in sync with the wheel and with what the school-wide moodboard counts.
export const resolveSecondaryCategory = (mood: string): string | undefined =>
  secondaryMoodWheel.find((category) => category.subEmotions.includes(mood))?.title;

// Normalizes the two journalsMap entry shapes (see JournalMapEntry above) into a single
// { rawMood, time } pair, or null if this entry isn't a mood check-in at all.
const NON_MOOD_LEGACY_ENTRY_TYPES = new Set(['struggle', 'comprehension']);

export const resolveJournalEntry = (entry: JournalMapEntry): { rawMood: string | number; time: number } | null => {
  if (entry.name === 'journal-mood-log') {
    const rawMood = entry.properties?.mood ?? entry.mood;
    if (rawMood === undefined || rawMood === null || entry.time === undefined) return null;
    return { rawMood, time: entry.time };
  }
  // A feeling/createdAt entry is either a legacy DynamoDB PeekavilleJournalEntry (type is the entry
  // kind: 'feelings-checkin'/'struggle'/'comprehension') or a result from the dedicated mood-only
  // Mixpanel query (type is the userType, e.g. 'Student'). Only the legacy non-mood entry kinds need
  // excluding explicitly - everything else with a feeling is a check-in.
  if (entry.feeling !== undefined && entry.feeling !== null && entry.createdAt !== undefined) {
    if (entry.type && NON_MOOD_LEGACY_ENTRY_TYPES.has(entry.type)) return null;
    return { rawMood: entry.feeling, time: entry.createdAt };
  }
  return null;
};

export const computeSchoolDeckMetrics = (input: SchoolDeckMetricsInput): SchoolDeckMetrics => {
  const {
    schoolName,
    startDate,
    endDate,
    legacyPeekavilleEvents,
    legacyPlatformEvents,
    newMixpanelEvents,
    gameProgressMap,
    assignmentsMap,
    teacherStudentMap,
    teacherClassroomMaps,
    userList,
    journalsMap,
  } = input;

  const allEvents = [...(legacyPeekavilleEvents || []), ...(legacyPlatformEvents || []), ...(newMixpanelEvents || [])];
  const studentsByTeacher = teacherStudentMap;

  const activeTeachers = getCurrentYearTeachersData(
    userList || [],
    teacherClassroomMaps,
    legacyPlatformEvents || [],
    newMixpanelEvents || [],
  );
  const sortedTeachers = [...activeTeachers].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  // Matches the on-page report's (AnalyticsOverview) "Active Users" / "Lessons Accessed" numbers
  // exactly - same source arrays, same helper functions - rather than a roster headcount
  // (activeTeachers/studentsByTeacher, which counts everyone assigned regardless of activity) or
  // an unfiltered event scan, either of which can disagree with what the on-page report shows.
  const staffEventsForOverview = [...(legacyPlatformEvents || []), ...(newMixpanelEvents || [])].filter((e) =>
    ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type),
  );
  const studentEventsForOverview = [
    ...(legacyPlatformEvents || []),
    ...(legacyPeekavilleEvents || []),
    ...(newMixpanelEvents || []),
  ].filter((e) => e.type === 'Student');

  const staffCount = getNumberOfEducators(staffEventsForOverview);
  const studentCount = getNumberOfStudents(studentEventsForOverview);
  const lessonsCount = getLegacyLessonsAccessed(staffEventsForOverview) + getNewLessonsAccessed(staffEventsForOverview);
  const checkinsCount = Object.values(journalsMap || {}).reduce(
    (total, entries) => total + (entries || []).filter((entry) => resolveJournalEntry(entry) !== null).length,
    0,
  );
  const assignmentsCount = Object.values(assignmentsMap || {}).flat().length;

  // Built from the same two separate legacy/new passes summed for the on-page report's "User Logins"
  // total (see the comment above isLegacyEducatorLoginEvent) - concatenating them (rather than a
  // single deduped filter) keeps sum(educatorLoginsByMonth) exactly equal to educatorLoginsTotal.
  const educatorLoginEventsLegacy = staffEventsForOverview.filter(isLegacyEducatorLoginEvent);
  const educatorLoginEventsNew = staffEventsForOverview.filter(isNewEducatorLoginEvent);
  const educatorLoginEvents = [...educatorLoginEventsLegacy, ...educatorLoginEventsNew];
  const studentLoginEventsLegacy = studentEventsForOverview.filter(isLegacyStudentLoginEvent);
  const studentLoginEventsNew = studentEventsForOverview.filter(isNewStudentLoginEvent);
  const studentLoginEvents = [...studentLoginEventsLegacy, ...studentLoginEventsNew];

  const educatorByMonth: Record<string, number> = {};
  educatorLoginEvents.forEach((event) => {
    const key = monthKeyOf(event.time);
    educatorByMonth[key] = (educatorByMonth[key] || 0) + 1;
  });
  const studentByMonth: Record<string, number> = {};
  studentLoginEvents.forEach((event) => {
    const key = monthKeyOf(event.time);
    studentByMonth[key] = (studentByMonth[key] || 0) + 1;
  });

  const allMonthKeys = [...new Set([...Object.keys(educatorByMonth), ...Object.keys(studentByMonth)])].sort();
  const rangeMonthKeys = monthsInRange(startDate, endDate);
  const monthKeys = rangeMonthKeys.length ? rangeMonthKeys : allMonthKeys;
  const educatorLoginsByMonth = monthKeys.map((key) => ({
    month: monthLabelOf(key),
    logins: educatorByMonth[key] || 0,
  }));
  const studentLoginsByMonth = monthKeys.map((key) => ({
    month: monthLabelOf(key),
    logins: studentByMonth[key] || 0,
  }));

  const dayCounts = Array(7).fill(0) as number[];
  educatorLoginEvents.forEach((event) => {
    const day = new Date(event.time).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
    const dayIndex = DAY_NAMES.indexOf(day);
    if (dayIndex >= 0) dayCounts[dayIndex]++;
  });
  const educatorDayOfWeek = weekdaysInOrder
    .map((day) => ({ day, count: dayCounts[DAY_NAMES.indexOf(day)] }))
    .filter((d) => d.count > 0);
  const mostUsedDay =
    educatorLoginEvents.length > 0
      ? weekdaysInOrder.reduce((best, day) =>
          dayCounts[DAY_NAMES.indexOf(day)] > dayCounts[DAY_NAMES.indexOf(best)] ? day : best,
        )
      : 'N/A';

  const topEducators = sortedTeachers
    .filter((teacher) => (teacher.loginTimes || 0) > 0)
    .map((teacher) => ({
      name: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
      logins: teacher.loginTimes || 0,
    }))
    .sort((a, b) => b.logins - a.logins)
    .slice(0, 5);

  const elementaryMoodCounts: Record<string, number> = {
    Excited: 0,
    Happy: 0,
    Calm: 0,
    Nervous: 0,
    Sad: 0,
    Angry: 0,
  };
  const secondaryMoodCounts: Record<string, number> = {
    Surprised: 0,
    Confident: 0,
    Happy: 0,
    Calm: 0,
    Sad: 0,
    Scared: 0,
    Angry: 0,
  };

  let totalElementaryCheckIns = 0;
  let totalSecondaryCheckIns = 0;

  const elementaryByMonth: Record<string, Record<string, number>> = {};
  const secondaryByMonth: Record<string, Record<string, number>> = {};
  const emptyElementaryMonth = () => ({
    Excited: 0,
    Happy: 0,
    Calm: 0,
    Nervous: 0,
    Sad: 0,
    Angry: 0,
  });
  const emptySecondaryMonth = () => ({
    Surprised: 0,
    Confident: 0,
    Happy: 0,
    Calm: 0,
    Sad: 0,
    Scared: 0,
    Angry: 0,
  });
  const ensureElementaryMonth = (month: string) => {
    if (!elementaryByMonth[month]) {
      elementaryByMonth[month] = {
        Excited: 0,
        Happy: 0,
        Calm: 0,
        Nervous: 0,
        Sad: 0,
        Angry: 0,
      };
    }
    return elementaryByMonth[month];
  };
  const ensureSecondaryMonth = (month: string) => {
    if (!secondaryByMonth[month]) {
      secondaryByMonth[month] = {
        Surprised: 0,
        Confident: 0,
        Happy: 0,
        Calm: 0,
        Sad: 0,
        Scared: 0,
        Angry: 0,
      };
    }
    return secondaryByMonth[month];
  };

  // Grade-band bucketing follows the school-wide moodboard, which sorts each journal-mood-log event
  // by the grade the check-in itself carries (entry.grade, sourced from the event's grade property).
  // Preferring that grade keeps this deck in step with the moodboard even when the current roster
  // grade differs from a student's grade at check-in time (mixed-grade classrooms, promoted or
  // transferred students). The roster grade is only a fallback for entries that carry none - chiefly
  // legacy DynamoDB journal entries whose records have no grade. A roster grade that fails to parse
  // (e.g. an ungraded classroom whose c.grade field is empty) must count as absent too; `rosterGrade
  // ?? entry.grade` would let a NaN win the nullish check and silently drop check-ins the moodboard
  // still counts. A check-in with no usable grade is dropped, matching how the moodboard drops
  // grade-less events.
  const parseGrade = (raw: unknown): number | undefined => {
    const grade = parseInt(String(raw ?? ''), 10);
    return Number.isNaN(grade) ? undefined : grade;
  };

  const gradeLookup = new Map<string, number | string | undefined>();
  Object.values(studentsByTeacher || {})
    .flat()
    .forEach((student) => {
      gradeLookup.set(student.userId, student.grade);
    });

  // Distinct students actually represented by a counted check-in - the "representative of N students"
  // caption describes this population, not the full roster headcount (all of a school's moodboard
  // check-ins are counted whether or not the student still appears on the roster, so a roster-based
  // count would undercount them too).
  const elementaryCheckedInUsers = new Set<string>();
  const secondaryCheckedInUsers = new Set<string>();

  Object.entries(journalsMap || {}).forEach(([userId, entries]) => {
    const rosterGrade = gradeLookup.get(userId);
    (entries || []).forEach((entry) => {
      const resolved = resolveJournalEntry(entry);
      if (!resolved) return;
      const gradeNum = parseGrade(entry.grade) ?? parseGrade(rosterGrade);
      if (gradeNum === undefined) return;
      const month = monthKeyOf(resolved.time);
      if (gradeNum <= 5) {
        const mood = resolveElementaryMood(resolved.rawMood);
        if (mood && Object.prototype.hasOwnProperty.call(elementaryMoodCounts, mood)) {
          elementaryMoodCounts[mood]++;
          totalElementaryCheckIns++;
          ensureElementaryMonth(month)[mood]++;
          elementaryCheckedInUsers.add(userId);
        }
      } else if (gradeNum >= 6) {
        const category = typeof resolved.rawMood === 'string' ? resolveSecondaryCategory(resolved.rawMood) : undefined;
        if (category && Object.prototype.hasOwnProperty.call(secondaryMoodCounts, category)) {
          secondaryMoodCounts[category]++;
          totalSecondaryCheckIns++;
          ensureSecondaryMonth(month)[category]++;
          secondaryCheckedInUsers.add(userId);
        }
      }
    });
  });

  const sortedMoodMonths = monthKeys;
  const elementaryMoodsByMonth = sortedMoodMonths.map((month) => ({
    month: monthLabelOf(month),
    counts: elementaryByMonth[month] || emptyElementaryMonth(),
  }));
  const secondaryMoodsByMonth = sortedMoodMonths.map((month) => ({
    month: monthLabelOf(month),
    counts: secondaryByMonth[month] || emptySecondaryMonth(),
  }));

  const elementaryMoods = ELEMENTARY_MOODS.map((mood) => ({
    mood,
    percent: totalElementaryCheckIns ? Math.round((elementaryMoodCounts[mood] / totalElementaryCheckIns) * 100) : 0,
  }));
  const elementaryUpMoodTotal =
    elementaryMoodCounts['Excited'] + elementaryMoodCounts['Happy'] + elementaryMoodCounts['Calm'];
  const elementaryUpMoodPercent = totalElementaryCheckIns
    ? Math.round((elementaryUpMoodTotal / totalElementaryCheckIns) * 100)
    : 0;

  const secondaryMoods = SECONDARY_MOOD_CATEGORIES.map((mood) => ({
    mood,
    percent: totalSecondaryCheckIns ? Math.round((secondaryMoodCounts[mood] / totalSecondaryCheckIns) * 100) : 0,
  }));
  const secondaryUpMoodTotal =
    secondaryMoodCounts['Surprised'] +
    secondaryMoodCounts['Confident'] +
    secondaryMoodCounts['Happy'] +
    secondaryMoodCounts['Calm'];
  const secondaryUpMoodPercent = totalSecondaryCheckIns
    ? Math.round((secondaryUpMoodTotal / totalSecondaryCheckIns) * 100)
    : 0;

  const elementaryStudents = elementaryCheckedInUsers.size;
  const secondaryStudents = secondaryCheckedInUsers.size;

  const gameAssociations = (associationData as { associations: Record<string, { displayName: string }> }).associations;
  const getDisplayName = (activityId: string): string => {
    const entry = gameAssociations[activityId];
    if (entry) return entry.displayName;
    const parts = activityId.match(/([A-Z]?[a-z]+)/g);
    if (parts) return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return activityId;
  };

  const gameCounts: Record<string, number> = {};
  Object.entries(gameProgressMap || {}).forEach(([, progress]) => {
    if (!Array.isArray(progress)) return;
    (progress as any[]).forEach((entry) => {
      if (!entry) return;
      const isCounted =
        (entry.type === 'quest' && entry.completionPercent > 0) ||
        (['forever', 'daily', 'finite'].includes(entry.type) && entry.timeSpent > 0);
      if (isCounted) {
        const name = getDisplayName(entry.displayName || entry.id || '');
        gameCounts[name] = (gameCounts[name] || 0) + 1;
      }
    });
  });
  const topGames = Object.entries(gameCounts)
    .map(([name, plays]) => ({ name, plays }))
    .sort((a, b) => b.plays - a.plays)
    .slice(0, 10);
  const mostPopularGame = topGames.length > 0 ? topGames[0].name : 'N/A';
  // Derived from the same gameProgressMap-based tally as topGames, rather than a separate raw
  // Mixpanel event-name filter - the two were computed from different sources and could disagree
  // (e.g. showing populated top-games bars alongside "0 games played").
  const activitiesCount = Object.values(gameCounts).reduce((sum, count) => sum + count, 0);

  const bookNumberToDisplayName: Record<string, string> = {};
  Object.entries(gameAssociations).forEach(([key, entry]) => {
    if (key.startsWith('book_')) {
      bookNumberToDisplayName[key] = entry.displayName;
    }
  });
  const storyEvents = [...getLegacyStoryOpenEvents(allEvents), ...getNewStoryOpenEvents(allEvents)];
  const storyCounts: Record<string, number> = {};
  storyEvents.forEach((event) => {
    const storyEvent = event as MoodLogEvent;
    let name = event.name;
    const book = storyEvent.properties?.book ?? storyEvent.book;
    if (book !== null && book !== undefined && bookNumberToDisplayName[`book_${book}`]) {
      name = bookNumberToDisplayName[`book_${book}`];
    } else if (storyEvent.properties?.unit || storyEvent.unit) {
      name = storyEvent.properties?.unit || storyEvent.unit || name;
    } else if (storyEvent.story) {
      name = storyEvent.story;
    } else if (storyEvent.quest) {
      name = storyEvent.quest;
    } else {
      const legacyMatch = event.name.match(/^story(\d+)pg1/);
      if (legacyMatch && bookNumberToDisplayName[`book_${legacyMatch[1]}`]) {
        name = bookNumberToDisplayName[`book_${legacyMatch[1]}`];
      } else {
        const featuredMatch = event.name.match(/^featuredstory[_ ](.+)/i);
        if (featuredMatch) {
          name = featuredMatch[1].replace(/[_-]/g, ' ');
        }
      }
    }
    storyCounts[name] = (storyCounts[name] || 0) + 1;
  });
  const topStories = Object.entries(storyCounts)
    .map(([name, reads]) => ({ name, reads }))
    .sort((a, b) => b.reads - a.reads)
    .slice(0, 10);
  // Matches the on-page report's "Story Opens" total exactly (Staff row + Student row) - topStories'
  // own list above scans allEvents unfiltered by type (matching MostReadStories.tsx, which does the
  // same), but the on-page report's numeric total is the sum of two type-filtered arrays, which can
  // differ from a plain allEvents count whenever a story-open event's type isn't Staff or Student.
  const storiesCount =
    getLegacyStoryOpens(staffEventsForOverview) +
    getNewStoryOpens(staffEventsForOverview) +
    getLegacyStoryOpens(studentEventsForOverview) +
    getNewStoryOpens(studentEventsForOverview);

  const dateRangeLabel = `${startDate ? startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''} - ${endDate ? endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}`;

  return {
    schoolName,
    dateRangeLabel,
    overview: {
      staff: staffCount,
      students: studentCount,
      lessons: lessonsCount,
      activities: activitiesCount,
      stories: storiesCount,
      checkins: checkinsCount,
      assignments: assignmentsCount,
    },
    educatorLoginsTotal: educatorLoginEvents.length,
    studentLoginsTotal: studentLoginEvents.length,
    educatorLoginsByMonth,
    studentLoginsByMonth,
    educatorDayOfWeek,
    mostUsedDay,
    topEducators,
    elementaryMoods,
    elementaryUpMoodPercent,
    elementaryStudents,
    elementaryMoodsByMonth,
    secondaryMoods,
    secondaryUpMoodPercent,
    secondaryStudents,
    secondaryMoodsByMonth,
    totalCheckIns: checkinsCount,
    topGames,
    mostPopularGame,
    topStories,
  };
};
